/**
 * Ticket domain schemas, derived from the generated Zod. One base serves both
 * the retrieve and list reads (identical shapes).
 *
 * THE UNION IS BUILT FROM A FLAT BASE. `ticketSchema` is a
 * `z.discriminatedUnion('entryType', [...])`, but the generated response is a
 * SINGLE FLAT object. Each arm is `TicketsRetrieveResponse.extend({...})`, so
 * the arms share one base and differ only in the discriminator plus the genuine
 * per-arm enrichments (callback requires `problemDescription`).
 *
 * Input rules — `requiredString` on `number`/`workPerformed`, the no-future and
 * year refinements on the timestamps — live on `ticketDtoSchema`, NOT the read
 * arms. A read arm must accept what the server actually returns.
 *
 * See ./README.md.
 */
import { z } from 'zod';

import {
  TicketsProblemTypesListResponse,
  TicketsRetrieveResponse,
} from '~/data/generated/zod/tickets/tickets.zod';
import { baseLinkedAttachmentSchema } from '~/data/schemas/attachment';
import { carDisplaySchema } from '~/data/schemas/car';
import { invoiceDisplaySchema } from '~/data/schemas/invoice';
import { type Portfolio } from '~/data/schemas/portfolio';

import {
  customErrorMap,
  noFutureDateTime,
  type OneOrMore,
  requiredString,
  VALID_DATE_YEAR_MESSAGE,
  validateDateYear,
  validateMaskHHmm,
  type Values,
} from './zodHelpers';

const ticketAttachmentSchema = baseLinkedAttachmentSchema;
export type TicketAttachment = z.infer<typeof ticketAttachmentSchema>;

/** `GET /tickets/problem-types/` — `name` is never null or absent; no override. */
export const ticketProblemTypesSchema = TicketsProblemTypesListResponse;
export type TicketProblemTypeOption = z.infer<typeof ticketProblemTypesSchema>[number];

/**
 *  Entry Type -  use options from the type table
 *  [Regular Callback (type callback), Emergency/Entrapment (type callback), Maintenance Visit, Deferred Callback (type callback)]
 *
 */

const EntryType = {
  Regular: 'regular',
  Emergency: 'emergency',
  Deferred: 'deferred',
  Visit: 'visit',
  Modernization: 'modernization',
  Repair: 'repair',
  Inspection: 'inspection',
} as const;

export const CallBackType = {
  Regular: 'regular',
  Emergency: 'emergency',
  Deferred: 'deferred',
} as const;

export const MaintenanceType = {
  Visit: 'visit',
  Modernization: 'modernization',
  Repair: 'repair',
  Inspection: 'inspection',
} as const;

export const entryTypeOptions = [
  [CallBackType.Regular, 'Regular Callback'],
  [CallBackType.Emergency, 'Emergency/Entrapment'],
  [CallBackType.Deferred, 'Deferred Callback'],
  [MaintenanceType.Visit, 'Maintenance Visit'],
  [MaintenanceType.Modernization, 'Modernization'],
  [MaintenanceType.Repair, 'Repair'],
  [MaintenanceType.Inspection, 'Inspection'],
] as const;
export const callbackToLabelMap = new Map(entryTypeOptions);

export function isOfCallBackType(key: string | CallbackTypeKey): key is CallbackTypeKey {
  return callbackTypeValues.some((val) => val === key);
}

export function ticketIsCallback(ticket: Ticket): ticket is CallbackTicket {
  return isOfCallBackType(ticket.entryType);
}

export const getWorkPerformedLabel = (entryType: string) => {
  const correctiveActionLabel = 'Corrective Action';
  const workPerformedLabel = 'Work Performed';
  return isOfCallBackType(entryType) ? correctiveActionLabel : workPerformedLabel;
};

type CallbackTypeKey = Values<typeof CallBackType>;
type MaintenanceTypeKey = Values<typeof MaintenanceType>;
export type EntryTypeKey = CallbackTypeKey | MaintenanceTypeKey;

export const callbackTypeValues = Object.values(CallBackType) as OneOrMore<CallbackTypeKey>;
const maintenanceTypeValues = Object.values(MaintenanceType) as OneOrMore<MaintenanceTypeKey>;
export const entryTypeValues = [
  ...callbackTypeValues,
  ...maintenanceTypeValues,
] as OneOrMore<EntryTypeKey>;

z.setErrorMap(customErrorMap);
// End custom validators

const decimalHoursSchema = z.number().min(0);

/**
 * Server ISO-8601 datetime grammar, as DRF's `DateTimeField` emits it:
 * `YYYY-MM-DDTHH:MM:SS`, optional fractional seconds, optional `Z`/±HH:MM
 * offset. Date and time components are range-bounded (month 01–12, day 01–31,
 * hour 00–23, minute/second 00–59) for the same reason
 * {@link SERVER_DURATION_RE} bounds its own: a read arm must accept everything
 * the server sends, but a value outside the grammar is real corruption and
 * should stay visible rather than pass silently.
 *
 * Deliberately NOT bounded on the YEAR. `\d{4}` accepts `0001` and `3025`
 * alike, because a year window is an app policy about user INPUT, not a fact
 * about the wire format — enforcing it here was the AMP-636 bug.
 *
 * The offset is optional even though `USE_TZ = True` means DRF always emits one
 * today. A naive ISO timestamp is still well-formed ISO-8601, not garbage, and
 * a read arm that rejects it would recreate the false raw-tier fallback this
 * schema exists to prevent.
 *
 * The offset also allows SECONDS (`±HH:MM:SS[.ffffff]`), which looks like
 * over-permissiveness and is not. DRF converts to the active timezone before
 * serializing, so under `TIME_ZONE = "UTC"` every value is `Z` today — but
 * point it at a named zone and any pre-1900 timestamp serializes against that
 * zone's LMT offset, which carries sub-minute precision
 * (`1883-11-17T07:03:58-04:56:02` for America/New_York). Pre-1900 values are
 * exactly the class AMP-636 was filed about, so a minutes-only offset would
 * re-arm this very bug the day someone activates a property timezone.
 */
const SERVER_ISO_DATETIME_RE =
  /^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,6})?(?:Z|[+-](?:[01]\d|2[0-3]):?[0-5]\d(?::[0-5]\d(?:\.\d{1,6})?)?)?$/;

/**
 * Read-arm `callTime`. The server (DRF DateTimeField) serializes an unset value
 * as `''`, which the generated strict `.datetime()` rejects, so collapse
 * `''`/absent/null to null. The app's 1900–2100 year rule is form INPUT
 * validation (see `formDateFields` / `formCallTimeField`), not a read
 * invariant: a real stored `callTime` outside that window surfaced as a
 * raw-tier "Ticket.*.callTime (custom)" Sentry error (AMP-636), exactly like
 * the `timeOnSite*` DurationField masks did before they moved form-side.
 *
 * Dropping the year rule is right; dropping ALL validation is not, or a
 * genuinely malformed timestamp would parse clean forever. The ISO-8601
 * grammar takes its place — every real value passes, non-ISO garbage does not
 * — mirroring `serverDurationField` below.
 */
const callTimeSchema = z
  .string()
  .nullish()
  .refine((val) => val == null || val.length === 0 || SERVER_ISO_DATETIME_RE.test(val), {
    message: 'Invalid datetime',
  })
  .transform((val) => (val != null && val.length > 0 ? val : null));

/**
 * Form-side `callTime` input validation (mirrors `formDateFields`): the user
 * edits a datetime-local value, so re-apply the 1900–2100 year rule the read
 * arm intentionally drops. Optional — `callTime` is not a required field, and an
 * unset value is `''`/null.
 */
const formCallTimeField = z
  .string()
  .nullish()
  .refine((val) => val == null || val.length === 0 || validateDateYear(val), {
    message: VALID_DATE_YEAR_MESSAGE,
  })
  .transform((val) => (val != null && val.length > 0 ? val : null));

/**
 * Server DurationField serialization: `[D ]HH:MM:SS[.ffffff]`. Django extracts
 * whole days first, so the HH:MM:SS part is always range-bounded (hours 00–23,
 * minutes/seconds 00–59); a value >= 24h carries a day prefix and a negative one
 * a leading `-`. Range-bounding (rather than accepting any digits) keeps genuine
 * corruption detectable on read instead of passing silently.
 */
const SERVER_DURATION_RE = /^-?(?:\d+\s)?(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,6})?$/;

/**
 * Read-arm schema for the four DurationField time columns. Accepts the server's
 * serialization (or null/absent/empty for an unset column), and nothing else —
 * a read arm must accept what the server returns, but a value outside the
 * DurationField grammar is real drift and should surface, not be swallowed. The
 * form re-applies the `HH:mm` input mask on top via `formTimeFields`.
 */
const serverDurationField = z
  .string()
  .nullish()
  .refine((val) => val == null || val.length === 0 || SERVER_DURATION_RE.test(val), {
    message: 'Invalid duration',
  });

/**
 * The generated ticket `property` object, reused as the base for the read
 * override below so its `.uuid()` refinements survive the widening.
 */
const generatedTicketProperty = TicketsRetrieveResponse.shape.property;

/**
 * Enrichments shared by both arms. `number`/`workPerformed`/`problemDescription`
 * are deliberately NOT required here — the API returns real records with them
 * empty or absent. They are required of the USER, form-side only.
 */
const sharedTicketEnrichments = {
  // Wire emits `property: null` (SET_NULL) — null-property tickets are real
  // production data. `TicketSerializer.property` is `allow_null=True` at source
  // as of AMP-633, so the generated base is already nullable — inherit it. The
  // nested `address`/`portfolio` are likewise `allow_null` at source (AMP-619).
  // (Read-only concern: a null property is still rejected on WRITE.)
  property: generatedTicketProperty,
  // Generated CarDisplay element ({id, name}); default [] so the parsed value is
  // always an array (the generated read types carsServiced optional) and the UI
  // can `.map`/`.length` without a guard.
  carsServiced: z.array(carDisplaySchema).default([]),
  // A ticket's linked invoice serializes just {id, number}; keep the generated
  // nullish (a ticket may have no linked invoice, or a lean row may omit it).
  invoice: invoiceDisplaySchema.nullish(),
  // startedTime/finishedTime deliberately inherit the generated datetime: a
  // server legitimately returns a timestamp at/after the client clock (skew, a
  // just-started ticket). The no-future rule is form-only input validation.
  // callTime is lenient on read for the same reason (see `callTimeSchema`); the
  // year rule is re-applied form-side via `formCallTimeField`.
  callTime: callTimeSchema,
  // Backend DurationField columns, serialized as `[D ]HH:MM:SS[.ffffff]` — NOT
  // the form's `HH:mm` mask. Applying that mask here rejected real >= 24h
  // durations on READ and produced raw-tier "Ticket.*.timeOnSite* (custom)"
  // Sentry errors. `serverDurationField` accepts the DurationField grammar (see
  // above); the `HH:mm` input mask is re-applied form-side via `formTimeFields`.
  timeOnSiteRegularHours: serverDurationField,
  travelTimeRegularHours: serverDurationField,
  timeOnSiteOvertime: serverDurationField,
  travelTimeOvertime: serverDurationField,
  attachments: z.array(ticketAttachmentSchema).default([]),
  autoUpload: z.boolean().default(false),
  equipmentFailure: z.boolean().nullable().default(null),
  // App-only field (not on the generated read); kept optional so reads without
  // it still parse and the form can carry it.
  maintenanceHours: decimalHoursSchema.optional(),
} as const;

const maintenanceTicketSchema = TicketsRetrieveResponse.extend({
  ...sharedTicketEnrichments,
  entryType: z.enum(maintenanceTypeValues),
});

const callbackTicketSchema = TicketsRetrieveResponse.extend({
  ...sharedTicketEnrichments,
  entryType: z.enum(callbackTypeValues),
});

export const ticketSchema = z.discriminatedUnion('entryType', [
  maintenanceTicketSchema,
  callbackTicketSchema,
]);

export const isTicketAutoUpload = (t: Ticket): boolean => {
  return t.autoUpload;
};

// Create base API request fields
/**
 * Base fields required for ticket API requests.
 * @property property - ID of the associated property
 * @property invoice - ID of the linked invoice, or null if unlinked
 * @property carsServiced - Array of car IDs serviced in this ticket
 * @property serviceProviderGroup - ID of the service provider group, or null if none
 */
const apiRequestFields = z.object({
  property: z.string(),
  invoice: z.string().nullable(),
  carsServiced: z.array(z.string()),
  serviceProviderGroup: z.string().nullable(),
});

// Schema for API requests
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const ticketApiRequestSchema = z.discriminatedUnion('entryType', [
  maintenanceTicketSchema
    .omit({
      property: true,
      carsServiced: true,
      serviceProviderGroup: true,
      invoice: true,
      id: true,
      // Read-only, server-computed (holiday calendar); never accepted on write.
      startedTimeHolidayName: true,
      // Server-owned timestamps the read arm now inherits from the generated
      // base (AMP-621 strict-schema drift fix) — never accepted on write.
      createdAt: true,
      updatedAt: true,
    })
    .merge(apiRequestFields)
    .extend({
      id: z.string().optional(),
      attachments: z.array(ticketAttachmentSchema).optional(),
    }),
  callbackTicketSchema
    .omit({
      property: true,
      carsServiced: true,
      serviceProviderGroup: true,
      invoice: true,
      id: true,
      // Read-only, server-computed (holiday calendar); never accepted on write.
      startedTimeHolidayName: true,
      // Server-owned timestamps the read arm now inherits from the generated
      // base (AMP-621 strict-schema drift fix) — never accepted on write.
      createdAt: true,
      updatedAt: true,
    })
    .merge(apiRequestFields)
    .extend({
      id: z.string().optional(),
      attachments: z.array(ticketAttachmentSchema).optional(),
    }),
]);

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const DisabledFieldsSchema = z.object({
  number: z.boolean().optional(),
  entryType: z.boolean().optional(),
  propertyId: z.boolean().optional(),
  serviceProviderGroup: z.boolean().optional(),
  carIds: z.boolean().optional(),
  invoiceId: z.boolean().optional(),
  workPerformed: z.boolean().optional(),
  callTime: z.boolean().optional(),
  startedTime: z.boolean().optional(),
  finishedTime: z.boolean().optional(),
  problemDescription: z.boolean().optional(),
  equipmentFailure: z.boolean().nullable().default(null),
  notes: z.boolean().optional(),
  timeOnSiteRegularHours: z.boolean().optional(),
  travelTimeRegularHours: z.boolean().optional(),
  timeOnSiteOvertime: z.boolean().optional(),
  travelTimeOvertime: z.boolean().optional(),
  problemType: z.boolean().optional(),
  maintenanceHours: z.boolean().optional(),
});

const dtoFields = {
  id: z.string().optional(),
  propertyId: z.string(),
  invoiceId: z.string().nullable(),
  carIds: z.array(z.string()),
  serviceProviderGroup: z.string().min(1, 'Service Provider is required'),
};

const omittedFieldsForForm = {
  property: true,
  carsServiced: true,
  invoice: true,
  attachments: true,
  // Server-computed, read-only field (`read_only_fields` on the ticket
  // serializer): the API fills it from the holiday calendar and never accepts
  // it on write. The form neither renders nor submits it, so it must not be a
  // form DTO field — the generated read arm types it `z.string().nullable()`
  // (nullable but NOT optional), which rejected the absent value on Edit and
  // silently blocked Save (AMP-585 regression). Read arms keep it required.
  startedTimeHolidayName: true,
  // Server-owned timestamps the read arm now inherits from the generated base
  // (AMP-621 strict-schema drift fix). `mapTicketToFormValues` never carries
  // them, so the form DTO must not require them either.
  createdAt: true,
  updatedAt: true,
} as const;

// Fields the create/edit form requires the user to fill. The read arms inherit
// the generated-optional shape (so reads stay drift-safe); the form re-tightens
// them here. `problemDescription` is required only on the callback arm.
const formRequiredFields = {
  number: requiredString.max(20),
  workPerformed: requiredString,
};

// The create/edit form takes naive datetime-local strings and enforces the app's
// no-future + 1900–2100 input rules. This is INPUT validation, not a read
// invariant, so it lives on the form arms only — the read arms inherit the
// generated datetime and must not throw on a server timestamp ahead of the
// client clock.
const formDateFields = {
  startedTime: noFutureDateTime().refine(validateDateYear, { message: VALID_DATE_YEAR_MESSAGE }),
  finishedTime: noFutureDateTime().refine(validateDateYear, { message: VALID_DATE_YEAR_MESSAGE }),
};

// Form-side time validation. A time field is pre-filled from the server's
// DurationField string but a user types the `HH:mm` mask, so the form must
// accept BOTH — otherwise editing a ticket with a >= 24h recorded time (a
// day-prefixed value like `1 02:00:00`, which the `HH:mm` mask rejects) blocks
// Save on a field the user never touched and cannot express as HH:mm. Genuinely
// malformed input is still rejected. This is INPUT validation (like
// `formDateFields`), so it lives on the form arms only; the read arms validate
// the server grammar via `serverDurationField` (see `sharedTicketEnrichments`).
const formTimeField = () =>
  z
    .string()
    .nullish()
    .refine(
      (val) =>
        val == null || val.length === 0 || validateMaskHHmm(val) || SERVER_DURATION_RE.test(val),
      { message: 'Invalid Time' },
    );

const formTimeFields = {
  timeOnSiteRegularHours: formTimeField(),
  travelTimeRegularHours: formTimeField(),
  timeOnSiteOvertime: formTimeField(),
  travelTimeOvertime: formTimeField(),
};

export const resolveTimeLabels = (entryType: string) => {
  if (entryType === EntryType.Regular || entryType === EntryType.Emergency) {
    return {
      startedLabel: 'Response Time',
      finishedLabel: 'Resolution Time',
    };
  }

  return {
    startedLabel: 'Arrival Time',
    finishedLabel: 'Departure Time',
  };
};

export const ticketDtoSchema = z
  .discriminatedUnion('entryType', [
    maintenanceTicketSchema
      .extend(dtoFields)
      .extend(formRequiredFields)
      .extend(formDateFields)
      .extend({ callTime: formCallTimeField })
      .extend(formTimeFields)
      .omit(omittedFieldsForForm),
    callbackTicketSchema
      .extend(dtoFields)
      .extend(formRequiredFields)
      .extend(formDateFields)
      .extend({ callTime: formCallTimeField })
      .extend(formTimeFields)
      // Callbacks require a problem description from the user; the read path
      // inherits the generated optional string.
      .extend({ problemDescription: requiredString })
      .omit(omittedFieldsForForm),
  ]) // TODO: Put this on ticketSchema as well
  // IMPORTANT NOTE: This superRefine gets triggered until the schema has no errors
  .superRefine((val, ctx) => {
    if (
      val.entryType === MaintenanceType.Visit ||
      val.entryType === MaintenanceType.Repair ||
      val.entryType === MaintenanceType.Inspection ||
      val.entryType === MaintenanceType.Modernization
    ) {
      const started = new Date(val.startedTime);
      const finished = new Date(val.finishedTime);
      const { startedLabel, finishedLabel } = resolveTimeLabels(val.entryType);
      if (started > finished) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_date,
          message: `${startedLabel} must be before ${finishedLabel}`,
          path: ['startedTime'],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_date,
          message: `${finishedLabel} must be after ${startedLabel}`,
          path: ['finishedTime'],
        });
      }
    }
  });

export type Ticket = z.infer<typeof ticketSchema>;
// `portfolio` is nullable: it is flattened off `ticket.property.portfolio`, and
// both the property and its portfolio can legitimately be absent — a ticket whose
// Property was deleted (`Ticket.property` is `SET_NULL`) has no portfolio to
// flatten, and a property with no portfolio emits an id-less object we normalize
// to null.
export type TicketWithPortfolio = Ticket & { portfolio: Portfolio | null };
type CallbackTicket = z.infer<typeof callbackTicketSchema>;
export type TicketDto = z.infer<typeof ticketDtoSchema>;
// What the ticket form's fields hold. `autoUpload` carries a `.default()`, so
// it is absent from the values until the resolver parses them.
export type TicketDtoValues = z.input<typeof ticketDtoSchema>;
export type API_TicketCreateRequest = z.infer<typeof ticketApiRequestSchema>;
export type DisabledFields = z.infer<typeof DisabledFieldsSchema>;

///////////////////////////////////////////////////////////////////////////////
// Ticket API response types
//
// They build on `Ticket`, so they live in this module rather than in a
// separate response-types file.

const localDateStringSchema = z
  .string()
  .min(10)
  .transform((val) => (val.length === 10 ? val + 'T00:00:00' : val))
  .transform((val) => new Date(val));

export type TicketListMetadata = {
  numTickets: number;
  timeOnSiteSeconds: number;
  maintenanceSeconds: number;
  regular: number;
  emergency: number;
  deferred: number;
};
export type API_TicketFilterResponse = {
  results: Ticket[];
  count: number;
  metadata: TicketListMetadata;
};
export type API_TicketDetailResponse = Ticket;
export const API_TicketFilterSearchParams = z.object({
  limit: z.number().optional(),
  offset: z.number().optional(),
  ordering: z.string().optional().default('-startedTime'),
  propertyName: z.string().optional(),
  property: z.string().optional(),
  carId: z.string().optional(),
  // Multi-car variant of `carId`, for the ticket history of a *set* of
  // elevators in one request (the invoice detail page's "all tickets for these
  // elevators"). Maps to the repeatable `cars` query param.
  cars: z.string().array().optional(),
  portfolios: z.string().array().optional(),
  serviceProviders: z.string().array().optional(),
  regions: z.string().array().optional(),
  entryType: z.string().array().optional(),
  search: z.string().optional(),
  startedTimeBefore: localDateStringSchema.optional(),
  startedTimeAfter: localDateStringSchema.optional(),
  number: z.string().optional(),
});
export type API_TicketFilterSearchParams = z.input<typeof API_TicketFilterSearchParams>;
